import { z } from "zod";

// =============================================================================
// Primitive Schemas
// =============================================================================

export const IdSchema = z.string().min(1);
export const JsonPathSchema = z.string().min(1);

export const PrimitiveLiteralSchema = z.union([
  z.string(),
  z.number(),
  z.boolean(),
  z.null(),
]);

export const JsonValueSchema: z.ZodType<JsonValue> = z.lazy(() =>
  z.union([
    PrimitiveLiteralSchema,
    z.array(JsonValueSchema),
    z.record(z.string(), JsonValueSchema),
  ])
);

export type JsonValue =
  | string
  | number
  | boolean
  | null
  | JsonValue[]
  | { [key: string]: JsonValue };

export const ComparisonOperatorSchema = z.enum([
  "eq",
  "neq",
  "gt",
  "gte",
  "lt",
  "lte",
]);
export type ComparisonOperator = z.infer<typeof ComparisonOperatorSchema>;

export const LogicalOperatorSchema = z.enum(["and", "or", "not"]);
export type LogicalOperator = z.infer<typeof LogicalOperatorSchema>;

export const ValueTypeSchema = z.enum([
  "string",
  "number",
  "boolean",
  "date",
  "datetime",
  "array",
  "object",
  "null",
  "unknown",
]);
export type ValueType = z.infer<typeof ValueTypeSchema>;

export const MissingValuePolicySchema = z.enum([
  "unknown",
  "fail",
  "pass",
  "null",
]);
export type MissingValuePolicy = z.infer<typeof MissingValuePolicySchema>;

export const TriStateSchema = z.enum(["true", "false", "unknown"]);
export type TriState = z.infer<typeof TriStateSchema>;

export const DecisionModeSchema = z.enum([
  "boolean",
  "tri_state",
  "multi_outcome",
  "scorecard",
]);
export type DecisionMode = z.infer<typeof DecisionModeSchema>;

export const SeveritySchema = z.enum([
  "info",
  "low",
  "medium",
  "high",
  "critical",
]);
export type Severity = z.infer<typeof SeveritySchema>;

export const ConfidenceSchema = z.number().min(0).max(1);

// =============================================================================
// AstExpression — Recursive Tree (canonical persisted format)
// =============================================================================

export interface AstExpression {
  type: string;
  _nodeId?: string;
  label?: string;

  // literal
  value?: JsonValue;
  valueType?: ValueType;

  // fact_ref / context_ref / field
  path?: string;
  required?: boolean;
  missingValuePolicy?: MissingValuePolicy;

  // comparison
  operator?: string;
  left?: AstExpression;
  right?: AstExpression;

  // logical
  children?: AstExpression[];

  // unary wrappers: exists, is_true, is_false, is_unknown
  target?: AstExpression;

  // case
  cases?: Array<{ when: AstExpression; then: AstExpression }>;
  else?: AstExpression;

  // scorecard
  factors?: Array<{
    id?: string;
    label: string;
    when: AstExpression;
    points: number;
    reason?: string;
  }>;
  bands?: ScoreBand[];

  // membership
  set?: AstExpression[];

  // function
  name?: string;
  arguments?: AstExpression[];

  // legacy compat: unary operand
  operand?: AstExpression;
}

export interface ScoreBand {
  min: number;
  max: number;
  label: string;
}

const ScoreBandSchema = z.object({
  min: z.number(),
  max: z.number(),
  label: z.string().min(1),
});

export const AstExpressionSchema: z.ZodType<AstExpression> = z.lazy(() =>
  z.object({
    type: z.string(),
    _nodeId: z.string().optional(),
    label: z.string().optional(),

    value: JsonValueSchema.optional(),
    valueType: ValueTypeSchema.optional(),

    path: z.string().optional(),
    required: z.boolean().optional(),
    missingValuePolicy: MissingValuePolicySchema.optional(),

    operator: z.string().optional(),
    left: AstExpressionSchema.optional(),
    right: AstExpressionSchema.optional(),

    children: z.array(AstExpressionSchema).optional(),

    target: AstExpressionSchema.optional(),

    cases: z
      .array(
        z.object({
          when: AstExpressionSchema,
          then: AstExpressionSchema,
        })
      )
      .optional(),
    else: AstExpressionSchema.optional(),

    factors: z
      .array(
        z.object({
          id: z.string().optional(),
          label: z.string().min(1),
          when: AstExpressionSchema,
          points: z.number(),
          reason: z.string().optional(),
        })
      )
      .optional(),
    bands: z.array(ScoreBandSchema).optional(),

    set: z.array(AstExpressionSchema).optional(),

    name: z.string().optional(),
    arguments: z.array(AstExpressionSchema).optional(),

    operand: AstExpressionSchema.optional(),
  }).passthrough()
);

// Known v1 expression node types
export const V1_NODE_TYPES = [
  "literal",
  "fact_ref",
  "context_ref",
  "field",
  "comparison",
  "logical",
  "binary",
  "unary",
  "exists",
  "existence",
  "is_true",
  "is_false",
  "is_unknown",
  "case",
  "scorecard",
  "membership",
  "function",
] as const;

// =============================================================================
// Rule Element
// =============================================================================

export interface LegislationReference {
  section?: string;
  subsection?: string;
  paragraph?: string;
  clause?: string;
}

export interface ExplainTemplates {
  pass?: string;
  fail?: string;
  unknown?: string;
}

export interface EvidenceRequirements {
  minConfidence?: number;
  requiredSources?: string[];
}

export const RuleElementSchema = z.object({
  id: IdSchema,
  label: z.string().min(1),
  expression: AstExpressionSchema,
  legislationReference: z
    .object({
      section: z.string().optional(),
      subsection: z.string().optional(),
      paragraph: z.string().optional(),
      clause: z.string().optional(),
    })
    .optional(),
  explainTemplates: z
    .object({
      pass: z.string().optional(),
      fail: z.string().optional(),
      unknown: z.string().optional(),
    })
    .optional(),
  evaluationStrategy: z.enum(["ast", "llm_classifier", "hybrid"]).optional(),
  inputsRequired: z.array(z.string()).optional(),
  evidenceRequirements: z
    .object({
      minConfidence: z.number().min(0).max(1).optional(),
      requiredSources: z.array(z.string()).optional(),
    })
    .optional(),
});

export type RuleElement = z.infer<typeof RuleElementSchema>;

// =============================================================================
// Obligations, Flags, Reasons
// =============================================================================

export const ObligationSchema = z.object({
  id: IdSchema,
  type: z.enum([
    "request_document",
    "request_human_review",
    "collect_fact",
    "notify",
    "block_action",
    "create_task",
    "invoke_capability",
  ]),
  title: z.string().min(1),
  description: z.string().optional(),
  severity: SeveritySchema.default("medium"),
  blocking: z.boolean().default(false),
  params: z.record(z.string(), JsonValueSchema).default({}),
});
export type Obligation = z.infer<typeof ObligationSchema>;

export const DecisionFlagSchema = z.object({
  code: z.string().min(1),
  label: z.string().min(1),
  severity: SeveritySchema.default("low"),
  message: z.string().optional(),
});
export type DecisionFlag = z.infer<typeof DecisionFlagSchema>;

export const DecisionReasonSchema = z.object({
  code: z.string().min(1),
  message: z.string().min(1),
  sourceNodeId: IdSchema.optional(),
  confidence: ConfidenceSchema.optional(),
});
export type DecisionReason = z.infer<typeof DecisionReasonSchema>;

// =============================================================================
// Emit Rules
// =============================================================================

export const EmitPayloadSchema = z.object({
  outcome: z.string().optional(),
  approved: z.boolean().optional(),
  score: z.number().optional(),
  band: z.string().optional(),
  flags: z.array(DecisionFlagSchema).default([]),
  reasons: z.array(DecisionReasonSchema).default([]),
  obligations: z.array(ObligationSchema).default([]),
  data: z.record(z.string(), JsonValueSchema).default({}),
});
export type EmitPayload = z.infer<typeof EmitPayloadSchema>;

export const EmitRuleSchema = z.object({
  id: IdSchema,
  label: z.string().optional(),
  when: AstExpressionSchema,
  emit: EmitPayloadSchema,
  stopProcessing: z.boolean().default(false),
});
export type EmitRule = z.infer<typeof EmitRuleSchema>;

// =============================================================================
// Decision Definition (top-level canonical document)
// =============================================================================

export const DecisionDefinitionSchema = z.object({
  schemaVersion: z.string().default("1.0"),
  kind: z.literal("decision_definition").default("decision_definition"),

  metadata: z.object({
    id: IdSchema,
    name: z.string().min(1),
    version: z.string().default("0.0.1"),
    description: z.string().optional(),
    tags: z.array(z.string()).default([]),
    mode: DecisionModeSchema.default("tri_state"),
  }),

  schemas: z
    .object({
      inputSchemaRef: z.string().optional(),
      outputSchemaRef: z.string().optional(),
    })
    .default({}),

  settings: z
    .object({
      traceEnabledByDefault: z.boolean().default(true),
      strictTyping: z.boolean().default(false),
      defaultMissingValuePolicy: MissingValuePolicySchema.default("unknown"),
    })
    .default({
      traceEnabledByDefault: true,
      strictTyping: false,
      defaultMissingValuePolicy: "unknown" as const,
    }),

  root: AstExpressionSchema,
  ruleElements: z.array(RuleElementSchema).default([]),
  emitRules: z.array(EmitRuleSchema).default([]),
  scoreBands: z.array(ScoreBandSchema).default([]),
  namedOutputs: z.record(z.string(), AstExpressionSchema).default({}),
});

export type DecisionDefinition = z.infer<typeof DecisionDefinitionSchema>;

// =============================================================================
// Editor State (UI-only, keyed by _nodeId)
// =============================================================================

export const EditorNodeStateSchema = z.object({
  x: z.number(),
  y: z.number(),
  width: z.number().optional(),
  height: z.number().optional(),
  collapsed: z.boolean().optional(),
  color: z.string().optional(),
});
export type EditorNodeState = z.infer<typeof EditorNodeStateSchema>;

export const EditorGroupSchema = z.object({
  id: IdSchema,
  label: z.string().min(1),
  nodeIds: z.array(IdSchema),
});

export const DecisionEditorStateSchema = z.object({
  layoutVersion: z.number().int().positive().default(1),
  viewport: z
    .object({
      x: z.number().default(0),
      y: z.number().default(0),
      zoom: z.number().default(1),
    })
    .default({ x: 0, y: 0, zoom: 1 }),
  nodes: z.record(z.string(), EditorNodeStateSchema).default({}),
  groups: z.array(EditorGroupSchema).default([]),
});

export type DecisionEditorState = z.infer<typeof DecisionEditorStateSchema>;

// =============================================================================
// Capability Document (what's stored in decision.json)
// =============================================================================

export const DecisionCapabilityDocumentSchema = z.object({
  definition: DecisionDefinitionSchema,
  editorState: DecisionEditorStateSchema.optional(),
});

export type DecisionCapabilityDocument = z.infer<
  typeof DecisionCapabilityDocumentSchema
>;

// =============================================================================
// Evaluation Result (runtime output)
// =============================================================================

export const NodeEvaluationTraceSchema = z.object({
  nodeId: z.string().optional(),
  nodeType: z.string().min(1),
  label: z.string().optional(),
  result: JsonValueSchema.optional(),
  triState: TriStateSchema.optional(),
  sourceFacts: z.array(JsonPathSchema).default([]),
  missingFacts: z.array(JsonPathSchema).default([]),
  message: z.string().optional(),
});
export type NodeEvaluationTrace = z.infer<typeof NodeEvaluationTraceSchema>;

export const DecisionEvaluationResultSchema = z.object({
  decisionId: IdSchema,
  decisionVersion: z.string().min(1),

  rootResult: JsonValueSchema.optional(),
  rootTriState: TriStateSchema.optional(),

  outcome: z.string().optional(),
  approved: z.boolean().optional(),
  score: z.number().optional(),
  band: z.string().optional(),

  flags: z.array(DecisionFlagSchema).default([]),
  reasons: z.array(DecisionReasonSchema).default([]),
  obligations: z.array(ObligationSchema).default([]),
  data: z.record(z.string(), JsonValueSchema).default({}),

  missingFacts: z.array(JsonPathSchema).default([]),
  matchedEmitRuleIds: z.array(IdSchema).default([]),

  trace: z.array(NodeEvaluationTraceSchema).default([]),

  evaluatedAt: z.string(),
});

export type DecisionEvaluationResult = z.infer<
  typeof DecisionEvaluationResultSchema
>;

// =============================================================================
// Validation
// =============================================================================

export interface DefinitionValidationError {
  path: string;
  message: string;
  nodeId?: string;
}

/**
 * Validate a DecisionDefinition beyond Zod structural checks.
 * Tree structure is self-contained, so we only check semantic constraints.
 */
export function validateDecisionDefinition(
  def: DecisionDefinition
): DefinitionValidationError[] {
  const errors: DefinitionValidationError[] = [];

  if (!def.root) {
    errors.push({ path: "root", message: "Root expression is required" });
  }

  const ruleElementIds = new Set<string>();
  for (const re of def.ruleElements) {
    if (ruleElementIds.has(re.id)) {
      errors.push({
        path: `ruleElements`,
        message: `Duplicate rule element ID: "${re.id}"`,
      });
    }
    ruleElementIds.add(re.id);

    if (!re.expression) {
      errors.push({
        path: `ruleElements.${re.id}`,
        message: `Rule element "${re.label}" has no expression`,
      });
    }
  }

  for (const rule of def.emitRules) {
    if (!rule.when) {
      errors.push({
        path: `emitRules.${rule.id}.when`,
        message: `Emit rule "${rule.id}" has no when condition`,
      });
    }
  }

  return errors;
}

// =============================================================================
// Helpers
// =============================================================================

let _nextNodeId = 0;

export function generateNodeId(): string {
  return `n_${Date.now()}_${_nextNodeId++}`;
}

export function createLiteralExpression(
  value: JsonValue,
  valueType?: ValueType
): AstExpression {
  return {
    type: "literal",
    _nodeId: generateNodeId(),
    value,
    valueType,
  };
}

export function createFactRefExpression(
  path: string,
  valueType?: ValueType
): AstExpression {
  return {
    type: "fact_ref",
    _nodeId: generateNodeId(),
    path,
    valueType,
    missingValuePolicy: "unknown",
  };
}

export function createComparisonExpression(
  operator: ComparisonOperator,
  left: AstExpression,
  right: AstExpression
): AstExpression {
  return {
    type: "comparison",
    _nodeId: generateNodeId(),
    operator,
    left,
    right,
  };
}

export function createLogicalExpression(
  operator: LogicalOperator,
  children: AstExpression[]
): AstExpression {
  return {
    type: "logical",
    _nodeId: generateNodeId(),
    operator,
    children,
  };
}

export function createEmptyDefinition(
  id: string,
  name: string
): DecisionDefinition {
  return {
    schemaVersion: "1.0",
    kind: "decision_definition",
    metadata: {
      id,
      name,
      version: "0.0.1",
      tags: [],
      mode: "tri_state",
    },
    schemas: {},
    settings: {
      traceEnabledByDefault: true,
      strictTyping: false,
      defaultMissingValuePolicy: "unknown",
    },
    root: createLogicalExpression("and", [
      createComparisonExpression(
        "gte",
        createFactRefExpression("input.value", "number"),
        createLiteralExpression(0, "number")
      ),
    ]),
    ruleElements: [],
    emitRules: [
      {
        id: "emit_approve",
        label: "Approve",
        when: { type: "is_true", target: { type: "literal", value: true } },
        emit: {
          outcome: "approve",
          approved: true,
          flags: [],
          reasons: [
            {
              code: "APPROVED",
              message: "All criteria met",
            },
          ],
          obligations: [],
          data: {},
        },
        stopProcessing: true,
      },
      {
        id: "emit_review",
        label: "Review",
        when: { type: "is_unknown", target: { type: "literal", value: true } },
        emit: {
          outcome: "manual_review",
          approved: false,
          flags: [],
          reasons: [
            {
              code: "MANUAL_REVIEW",
              message: "Requires manual review",
            },
          ],
          obligations: [],
          data: {},
        },
        stopProcessing: true,
      },
      {
        id: "emit_decline",
        label: "Decline",
        when: { type: "is_false", target: { type: "literal", value: true } },
        emit: {
          outcome: "decline",
          approved: false,
          flags: [],
          reasons: [
            {
              code: "DECLINED",
              message: "Criteria not met",
            },
          ],
          obligations: [],
          data: {},
        },
        stopProcessing: true,
      },
    ],
    scoreBands: [],
    namedOutputs: {},
  };
}

export function createEmptyDocument(
  id: string,
  name: string
): DecisionCapabilityDocument {
  return {
    definition: createEmptyDefinition(id, name),
    editorState: {
      layoutVersion: 1,
      viewport: { x: 0, y: 0, zoom: 1 },
      nodes: {},
      groups: [],
    },
  };
}
